import React, { useState } from "react";
import { X } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import PromoCodeField from "@/components/PromoCodeField";
import { useConfig } from "@/lib/useConfig";

const TOOL_TIERS = [
  { key: "basic", name: "Basic Listing", price: 29, features: ["Standard placement in the marketplace", "Listed in relevant category", "Company profile link"] },
  { key: "featured", name: "Featured Listing", price: 59, popular: true, features: ["Top of your category", "Bold highlighted card", "Featured badge", "Included in one WorldAI Weekly newsletter"] },
  { key: "premium", name: "Premium Listing", price: 99, features: ["Homepage featured placement", "Top of all search results", "Featured badge", "Included in two WorldAI Weekly newsletters", "Social media mention"] },
];

const CATS = ["Chat & Assistants", "Coding & Development", "Image Generation", "Video Generation",
  "Audio & Voice", "Writing & Content", "Productivity & Automation", "Analytics & Research",
  "AI Infrastructure", "AI Agents", "AI Hardware", "E-Commerce"];

const inputCls = "w-full bg-[#111827] border border-white/10 rounded-md px-3 py-2.5 text-sm focus:border-[#00F0FF] focus:outline-none";

export default function ListToolModal({ open, onClose }) {
  const empty = { name: "", tagline: "", company: "", company_website: "", category: CATS[0],
    pricing_model: "Freemium", description: "", website: "", tags: "", contact_email: "" };
  const [form, setForm] = useState(empty);
  const [step, setStep] = useState("tier");
  const [tier, setTier] = useState(null);
  const [promo, setPromo] = useState(null);
  const [busy, setBusy] = useState(false);
  const { paymentsEnabled } = useConfig();
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const effPrice = tier ? (promo ? +(tier.price * (1 - promo.discount_pct / 100)).toFixed(2) : tier.price) : 0;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/tools/checkout", { form, tier: paymentsEnabled ? tier.key : "basic", promo_code: promo?.code, origin_url: window.location.origin });
      if (data.free || !data.url) {
        toast.success("Submitted! We'll review your tool within 24 hours and email you when it's live — you're on the WorldAI Weekly too.");
        close();
      } else {
        window.location.href = data.url;
      }
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
      setBusy(false);
    }
  };
  const close = () => { setForm(empty); setStep("tier"); setTier(null); setPromo(null); setBusy(false); onClose(); };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="list-tool-modal">
      <div className="card-surface rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto p-7 relative">
        <button onClick={close} className="absolute top-4 right-4 text-[#94A3B8] hover:text-white" data-testid="list-tool-close"><X className="w-5 h-5" /></button>
        {paymentsEnabled && step === "tier" ? (
          <div data-testid="list-tool-tiers">
            <h3 className="font-display font-bold text-2xl mb-1">List your AI tool</h3>
            <p className="text-sm text-[#94A3B8] mb-6">Choose a plan (one-time payment). Payment is required before your tool enters the review queue.</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {TOOL_TIERS.map((t) => (
                <div key={t.key} data-testid={`tool-tier-card-${t.key}`}
                  className={`rounded-xl p-5 border flex flex-col ${t.popular ? "border-[#00F0FF]/50 bg-[#00F0FF]/5" : "border-white/10 bg-[#0e1424]"}`}>
                  {t.popular && <span className="font-mono-tech text-[10px] uppercase text-[#00F0FF] mb-2">Most popular</span>}
                  <h4 className="font-display font-bold text-lg">{t.name}</h4>
                  <div className="font-display font-extrabold text-3xl text-[#00F0FF] my-2">${t.price}</div>
                  <ul className="space-y-1.5 text-xs text-[#94A3B8] flex-1 mb-4">
                    {t.features.map((f, i) => <li key={i} className="flex gap-1.5"><span className="text-[#22C55E]">✓</span> {f}</li>)}
                  </ul>
                  <button onClick={() => { setTier(t); setStep("form"); }} data-testid={`tool-tier-select-${t.key}`}
                    className={`w-full font-semibold px-4 py-2.5 rounded-md transition text-sm ${t.popular ? "bg-[#00F0FF] text-[#04121a] hover:brightness-110" : "border border-white/15 hover:border-[#00F0FF]/60"}`}>
                    Choose {t.name.split(" ")[0]}
                  </button>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
              <h3 className="font-display font-bold text-2xl">List your AI tool</h3>
              {paymentsEnabled && (
                <button onClick={() => { setStep("tier"); setPromo(null); }} data-testid="lt-change-tier" className="text-xs text-[#00F0FF] hover:underline">← Change plan</button>
              )}
            </div>
            <p className="text-sm text-[#94A3B8] mb-6">
              {paymentsEnabled && tier ? (
                <>
                  <span className="text-white font-medium">
                    {tier.name} — {promo ? (<><span className="line-through text-[#64748B]">${tier.price}</span> <span className="text-[#FBBF24]">${effPrice}</span></>) : <>${tier.price}</>}.
                  </span> Your tool will be reviewed and published within 24 hours after payment.
                </>
              ) : (
                <><span className="text-[#4ade80] font-medium">Free to list.</span> Every tool is reviewed by our team and published within 24 hours — no payment required.</>
              )}
            </p>
            <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="text-xs text-[#94A3B8] mb-1 block">Tool Name *</label>
                <input required value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls} data-testid="lt-name" />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs text-[#94A3B8] mb-1 block">Tagline</label>
                <input value={form.tagline} onChange={(e) => set("tagline", e.target.value)} className={inputCls} placeholder="One-line summary" data-testid="lt-tagline" />
              </div>
              <div>
                <label className="text-xs text-[#94A3B8] mb-1 block">Company Name *</label>
                <input required value={form.company} onChange={(e) => set("company", e.target.value)} className={inputCls} data-testid="lt-company" />
              </div>
              <div>
                <label className="text-xs text-[#94A3B8] mb-1 block">Company Website</label>
                <input value={form.company_website} onChange={(e) => set("company_website", e.target.value)} className={inputCls} placeholder="https://" data-testid="lt-company-website" />
              </div>
              <div>
                <label className="text-xs text-[#94A3B8] mb-1 block">Category *</label>
                <select value={form.category} onChange={(e) => set("category", e.target.value)} className={inputCls} data-testid="lt-category">
                  {CATS.map((c) => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-[#94A3B8] mb-1 block">Pricing Model</label>
                <select value={form.pricing_model} onChange={(e) => set("pricing_model", e.target.value)} className={inputCls} data-testid="lt-pricing">
                  {["Free", "Freemium", "Paid", "Subscription", "Usage-based", "Enterprise"].map((p) => <option key={p}>{p}</option>)}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="text-xs text-[#94A3B8] mb-1 block">Description *</label>
                <textarea required rows={4} value={form.description} onChange={(e) => set("description", e.target.value)} className={inputCls} data-testid="lt-description" />
              </div>
              <div>
                <label className="text-xs text-[#94A3B8] mb-1 block">Tool Website *</label>
                <input required value={form.website} onChange={(e) => set("website", e.target.value)} className={inputCls} placeholder="https://" data-testid="lt-website" />
              </div>
              <div>
                <label className="text-xs text-[#94A3B8] mb-1 block">Tags (comma separated)</label>
                <input value={form.tags} onChange={(e) => set("tags", e.target.value)} className={inputCls} placeholder="Chat, LLM, API" data-testid="lt-tags" />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs text-[#94A3B8] mb-1 block">Contact Email *</label>
                <input required type="email" value={form.contact_email} onChange={(e) => set("contact_email", e.target.value)} className={inputCls} data-testid="lt-contact-email" />
              </div>
              <div className="md:col-span-2 mt-2">
                <PromoCodeField onApplied={setPromo} />
              </div>
              <div className="md:col-span-2 mt-2">
                <button type="submit" disabled={busy}
                  className="w-full bg-[#00F0FF] text-[#04121a] font-semibold px-6 py-3 rounded-md hover:brightness-110 transition disabled:opacity-50" data-testid="lt-submit">
                  {busy ? (paymentsEnabled ? "Redirecting to secure payment…" : "Submitting…") : (paymentsEnabled ? `Continue to Payment — $${effPrice}` : "Submit Tool for Review")}
                </button>
                <p className="text-xs text-[#64748B] text-center mt-2">
                  {paymentsEnabled ? "Secure payment via Stripe. Your tool is reviewed after payment is confirmed." : "Free while we're in pre-launch. Every tool is reviewed before it goes live."}
                </p>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
